import { useEffect, useState } from "react";
import { baueAnschreiben, type AnschreibenDaten } from "../../shared/anschreiben.js";
import type { AngebotDetail } from "../../shared/types.js";
import { api, nachladeFehler } from "../api.js";

/**
 * Dokumentansicht eines Angebots: das PDF wird im Browser erzeugt und direkt
 * im Fenster angezeigt. Derselbe Blob dient dem Download — was der Kunde
 * bekommt, ist also genau das, was hier zu sehen ist.
 *
 * Daneben das Anschreiben als reiner Text zum Einfügen in eine Mail. Beides
 * aus demselben Datenstand: Das PDF ist die Anlage, der Brief die Mail.
 */

/**
 * Angebot → Anschreiben.
 *
 * Nur hier, weil nur diese Ansicht ein vollständiges Angebot hat. Die
 * Schnellkalkulation füttert denselben Baustein mit weniger Feldern; was
 * fehlt, lässt `baueAnschreiben` weg statt es zu erfinden.
 */
function alsAnschreiben(d: AngebotDetail): AnschreibenDaten {
  const a = d.angebot as Record<string, string | number | null>;
  const e = d.einstellungen;
  const text = (w: unknown) => (typeof w === "string" && w.trim() ? w.trim() : null);

  return {
    verbindlichkeit: a.verbindlichkeit === "verbindlich" ? "verbindlich" : "freibleibend",
    positionen: d.positionen.map((p) => ({
      bezeichnung: String(p.bezeichnung),
      // bauePositionInput setzt "—", wenn kein Gebinde gewählt ist. Im Brief
      // wäre der Gedankenstrich hinter der Mehltype ein Schreibfehler.
      verpackung: p.verpackung_text === "—" ? null : text(p.verpackung_text),
      menge_kg: Number(p.menge_kg),
      preis_cent: Number(p.endpreis_cent),
    })),
    laufzeit_von: a.vertragsart === "kontrakt" ? text(a.kontrakt_von) : null,
    laufzeit_bis: a.vertragsart === "kontrakt" ? text(a.kontrakt_bis) : null,
    lieferbedingung: a.lieferbedingung === "ab_werk" ? "ab_werk" : "frachtfrei_empfaenger",
    kunde_ort: text(a.kunde_ort),
    werk_ort: e.firma_plz_ort?.replace(/^\d{5}\s*/, "") || null,
    // „Sukzessive Abnahme" ergibt nur bei einem Kontrakt mit Abruf Sinn.
    sukzessive: a.vertragsart === "kontrakt" && !!a.abruf_rhythmus,
    teilpartie_min_kg: a.teilpartie_min_kg ? Number(a.teilpartie_min_kg) : null,
    zahlungsziel_tage: Number(a.zahlungsziel_tage) || 14,
    fusstext: text(e.angebot_fusstext),
    agb_hinweis: text(e.agb_hinweis),
    absender: [
      e.firma_name,
      [e.firma_strasse, e.firma_plz_ort].filter(Boolean).join(", "),
      e.firma_registergericht && e.firma_hra ? `${e.firma_registergericht} ${e.firma_hra}` : "",
      e.komplementaer_firma
        ? `Komplementärin: ${e.komplementaer_firma}${e.komplementaer_sitz ? `, ${e.komplementaer_sitz}` : ""}` +
          (e.komplementaer_hrb ? ` (${e.komplementaer_hrb})` : "")
        : "",
      e.geschaeftsfuehrer ? `Geschäftsführer: ${e.geschaeftsfuehrer}` : "",
      [e.firma_telefon, e.firma_email].filter(Boolean).join(" · "),
    ].filter(Boolean),
  };
}
export function AngebotAnsicht({
  id,
  beiZurueck,
}: {
  id: string;
  beiZurueck: () => void;
}) {
  const [detail, setDetail] = useState<AngebotDetail | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [name, setName] = useState("Angebot.pdf");
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    let widerrufen = false;
    let url: string | null = null;

    (async () => {
      try {
        const d = await api.get<AngebotDetail>(`/angebote/${id}`);
        if (widerrufen) return;
        setDetail(d);

        // jsPDF nur bei Bedarf laden — eigener Chunk, sonst trägt jede Seite
        // die ~350 kB mit.
        const { erzeugeAngebotsPdf, dateiname } = await import("../angebotPdf.js");
        const blob = await erzeugeAngebotsPdf(d);
        if (widerrufen) return;

        url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setName(dateiname(d.angebot.angebotsnr, d.angebot.kunde_firma));
      } catch (e) {
        if (!widerrufen) setFehler(nachladeFehler(e));
      }
    })();

    return () => {
      widerrufen = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  const fehlendePflichtangaben = detail
    ? (["komplementaer_firma", "komplementaer_hrb", "geschaeftsfuehrer"] as const).filter(
        (k) => !detail.einstellungen[k],
      )
    : [];

  function herunterladen() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = name;
    a.click();
  }

  const anschreiben = detail ? baueAnschreiben(alsAnschreiben(detail)) : "";

  async function kopieren() {
    await navigator.clipboard.writeText(anschreiben).catch(() => {});
    setKopiert(true);
    window.setTimeout(() => setKopiert(false), 2500);
  }

  return (
    <>
      <div className="knopfzeile" style={{ marginBottom: "1rem" }}>
        <button className="knopf sekundaer" onClick={beiZurueck}>
          ← Zurück zur Liste
        </button>
        <button className="knopf" onClick={herunterladen} disabled={!pdfUrl}>
          PDF herunterladen
        </button>
        <button className="knopf sekundaer" onClick={kopieren} disabled={!detail}>
          {kopiert ? "In der Zwischenablage" : "Anschreiben kopieren"}
        </button>
        {pdfUrl && (
          <a className="knopf sekundaer" href={pdfUrl} target="_blank" rel="noreferrer"
             style={{ textDecoration: "none", display: "inline-block" }}>
            In neuem Tab öffnen
          </a>
        )}
      </div>

      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}

      {fehlendePflichtangaben.length > 0 && (
        <div className="meldung hinweis">
          <strong>Pflichtangaben fehlen im Firmenkopf.</strong> Bei einer GmbH &amp; Co. KG
          verlangt § 125 HGB auf jedem Geschäftsbrief zusätzlich Firma, Sitz und HRB der
          Komplementär-GmbH sowie deren Geschäftsführer. Unter „Stammdaten → Firmenkopf“
          nachtragen, bevor das Angebot rausgeht.
        </div>
      )}

      {detail && (
        <details style={{ marginBottom: "1rem" }}>
          <summary className="hinweistext" style={{ cursor: "pointer" }}>
            Anschreiben ansehen — der Text für die Mail, das PDF ist die Anlage
          </summary>
          <pre className="textvorschau">{anschreiben}</pre>
        </details>
      )}

      {!pdfUrl && !fehler && <p className="leer">Angebot wird gesetzt …</p>}

      {pdfUrl && (
        <div className="pdf-rahmen">
          <iframe src={pdfUrl} title={`Angebot ${detail?.angebot.angebotsnr ?? ""}`} />
        </div>
      )}
    </>
  );
}
